import {
  createSlice,
  createEntityAdapter,
  EntityState,
  PayloadAction,
} from '@reduxjs/toolkit'
import { deserializeFilters } from '@user/components/Filters'

import { StoreData } from '../reducers/typings'
import { loadFilterPreferences } from '../userPreferences'
import { getSalesFunnelSummary } from './api'
import {
  defaultFilters,
  SalesFunnelFilterSet,
  normalizeSalesFunnelFilters,
  SalesFunnelSummaryFilterInput,
  FunnelFilterType,
} from './filters'
import { ISalesFunnelCard, ISalesFunnelSummaryCard } from './typings'

const funnelCardsAdapter = createEntityAdapter<ISalesFunnelCard>({
  selectId: (f) => f.salesFunnelId,
})

type SalesFunnelSummaryState = {
  filterSet: SalesFunnelFilterSet
  funnels: EntityState<ISalesFunnelCard>
  summary: ISalesFunnelSummaryCard | null
}

export const initialState: SalesFunnelSummaryState = {
  filterSet: defaultFilters,
  funnels: funnelCardsAdapter.getInitialState(),
  summary: null,
}

export const salesFunnelSummarySlice = createSlice({
  name: 'salesFunnelSummary',
  initialState,
  reducers: {
    setFilters: (state, { payload }: PayloadAction<SalesFunnelFilterSet>) => {
      state.filterSet = normalizeSalesFunnelFilters(payload)
    },
  },
  extraReducers: (builder) => {
    builder.addCase(getSalesFunnelSummary.fulfilled, (state, { payload }) => {
      state.summary = payload.salesFunnelSummary
      funnelCardsAdapter.upsertMany(state.funnels, payload.salesFunnelCards)
    }),
      builder.addCase(getSalesFunnelSummary.pending, (state) => {
        funnelCardsAdapter.removeAll(state.funnels)
      }),
      builder.addCase(loadFilterPreferences.fulfilled, (state, { payload }) => {
        const salesFunnelPref = payload.find(
          (p) => p.type === 'salesFunnelSummary',
        )
        state.filterSet = !!salesFunnelPref
          ? normalizeSalesFunnelFilters(
              deserializeFilters<FunnelFilterType, SalesFunnelFilterSet>(
                defaultFilters,
                salesFunnelPref.filter,
              ),
            )
          : defaultFilters
      })
  },
})

export default salesFunnelSummarySlice.reducer

export const { setFilters: setSalesFunnelSummaryFilters } =
  salesFunnelSummarySlice.actions

export const funnelCardSelectors = funnelCardsAdapter.getSelectors<StoreData>(
  (state) => state.salesFunnelSummary.funnels,
)

export const selectSalesFunnelSummary = (
  state: StoreData,
): ISalesFunnelSummaryCard | null => {
  return state.salesFunnelSummary.summary
}

export const selectSalesFunnelSummaryDateRange = (
  state: StoreData,
): SalesFunnelSummaryFilterInput => {
  return state.salesFunnelSummary.filterSet.inputModel
}

export const { selectAll: selectAllSalesFunnelCards } = funnelCardSelectors

export const selectSalesFunnelFilters = (state: StoreData) =>
  state.salesFunnelSummary.filterSet
